import { Bell, Edit3, Inbox, LogOut, Mail, Send, Settings2, Trash2, AlertTriangle, type LucideProps } from "lucide-react";
import type { Folder } from "../types";

export interface FolderSidebarProps {
    folders: Folder[];
    current: string | null;
    onSelect: (folder: string) => void;
    accountEmail: string;
    onLogout: () => void;
    onCompose: () => void;
    onSettings?: () => void;
    onEnableNotifications?: () => void;
}

export const ROLE_ICON: Record<string, React.ComponentType<LucideProps>> = {
    inbox: Inbox,
    sent: Send,
    drafts: Edit3,
    trash: Trash2,
    junk: AlertTriangle,
};

export function roleFor(folder: Folder): string {
    return folder.role ?? folder.fullName.toLowerCase();
}

export function FolderSidebar({ folders, current, onSelect, accountEmail, onLogout, onCompose, onSettings, onEnableNotifications }: FolderSidebarProps) {
    return (
        <aside className="flex h-full w-full shrink-0 flex-col border-r border-white/10 bg-[#0a0a0b]">
            {/* Brand */}
            <div className="flex items-center gap-2.5 px-4 pb-4 pt-5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-orange-500/20 bg-orange-500/10 text-sm text-orange-400">
                    ✦
                </div>
                <div className="leading-tight">
                    <div className="text-sm font-black tracking-tight text-white">DiscordForge Mail</div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
                        {(accountEmail || "").split("@")[1] || accountEmail || "mail"}
                    </div>
                </div>
            </div>

            {/* Compose */}
            <div className="px-3 pb-4">
                <button
                    type="button"
                    onClick={onCompose}
                    className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2.5 text-sm font-bold text-white shadow-[0_0_30px_rgba(249,115,22,0.2)] transition-all hover:from-orange-600 hover:to-amber-600 active:scale-[0.98]"
                >
                    <Edit3 size={15} />
                    Compose
                </button>
            </div>

            {/* Folders */}
            <nav className="custom-scrollbar flex-1 space-y-0.5 overflow-y-auto px-2.5 pb-3">
                {folders.map((folder) => {
                    const role = roleFor(folder);
                    const Icon = ROLE_ICON[role] ?? Mail;
                    const selected = folder.fullName === current;
                    return (
                        <button
                            key={folder.fullName}
                            type="button"
                            onClick={() => onSelect(folder.fullName)}
                            className={`group flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-[13px] font-medium transition-all ${
                                selected
                                    ? "border border-orange-500/25 bg-orange-500/10 text-orange-400"
                                    : "border border-transparent text-zinc-400 hover:bg-white/5 hover:text-zinc-100"
                            }`}
                        >
                            <span className="flex min-w-0 items-center gap-2.5">
                                <Icon size={15} className="shrink-0 opacity-70" />
                                <span className="truncate">{folder.name}</span>
                            </span>
                            <span className="flex shrink-0 items-center gap-1.5">
                                {folder.unreadEmails > 0 && (
                                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-black text-white shadow-[0_0_12px_rgba(249,115,22,0.4)]">
                                        {folder.unreadEmails}
                                    </span>
                                )}
                                {folder.totalEmails > 0 && folder.unreadEmails === 0 && (
                                    <span className="text-[10px] font-semibold tabular-nums text-zinc-600">
                                        {folder.totalEmails}
                                    </span>
                                )}
                            </span>
                        </button>
                    );
                })}
            </nav>

            {/* Account footer */}
            <div className="border-t border-white/10 p-3">
                <div className="flex items-center justify-between gap-2 rounded-xl px-2 py-1.5">
                    <div className="flex min-w-0 items-center gap-2.5">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0e0e11] text-[11px] font-black text-orange-400 ring-1 ring-white/10">
                            {((accountEmail || "?")[0] ?? "?").toUpperCase()}
                        </div>
                        <div className="min-w-0 leading-tight">
                            <div className="truncate text-xs font-semibold text-zinc-200">{accountEmail}</div>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Connected</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onLogout}
                        title="Sign out"
                        className="cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-red-400"
                    >
                        <LogOut size={14} />
                    </button>
                </div>
                <div className="mt-2 flex items-center gap-1 px-2">
                    {onEnableNotifications && (
                        <button
                            type="button"
                            onClick={onEnableNotifications}
                            title="Enable desktop notifications"
                            className="cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-orange-400"
                        >
                            <Bell size={14} />
                        </button>
                    )}
                    {onSettings && (
                        <button
                            type="button"
                            onClick={onSettings}
                            title="Settings"
                            className="cursor-pointer rounded-lg p-1.5 text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
                        >
                            <Settings2 size={14} />
                        </button>
                    )}
                    <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
                        Settings
                    </span>
                </div>
            </div>
        </aside>
    );
}
